import React, { useState, useEffect, useCallback } from "react";
import { getHistory, updateHistoryStatus } from "../services/api";
import styles from "./HistorySidebar.module.css";

const STATUS_OPTIONS = [
  { value: "",            label: "— Status —", icon: "" },
  { value: "sent",        label: "Sent",        icon: "📤" },
  { value: "replied",     label: "Replied",     icon: "✅" },
  { value: "no_response", label: "No Response", icon: "⏳" },
];

const HistorySidebar = ({ onSelect }) => {
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getHistory();
      setHistory(res.data.history || []);
    } catch {
      setError("Failed to load history");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchHistory();
  }, [open, fetchHistory]);

  const handleSelect = (item) => {
    if (onSelect) {
      onSelect({
        formData: item.input || {},
        message: item.output || "",
      });
    }
  };

  const handleStatusChange = async (e, itemId) => {
    e.stopPropagation();
    const newStatus = e.target.value || null;
    // Optimistic update
    setHistory((prev) =>
      prev.map((h) =>
        h._id === itemId ? { ...h, applicationStatus: newStatus } : h
      )
    );
    try {
      await updateHistoryStatus(itemId, newStatus);
    } catch {
      // Revert on failure
      fetchHistory();
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  };

  return (
    <>
      <button
        className={`${styles.toggleBtn} ${open ? styles.toggleBtnOpen : ""}`}
        onClick={() => setOpen((prev) => !prev)}
        title={open ? "Close history" : "Open history"}
        aria-label={open ? "Close history sidebar" : "Open history sidebar"}
      >
        {open ? "✕" : "📜"}
      </button>

      <div className={`${styles.sidebar} ${open ? styles.sidebarOpen : ""}`}>
        <div className={styles.header}>
          <h3 className={styles.title}>📜 History</h3>
          <button
            className={styles.refreshBtn}
            onClick={fetchHistory}
            disabled={loading}
            title="Refresh"
          >
            🔄
          </button>
        </div>

        <div className={styles.list}>
          {loading && (
            <div className={styles.statusMsg}>Loading…</div>
          )}

          {error && !loading && (
            <div className={styles.statusMsg}>{error}</div>
          )}

          {!loading && !error && history.length === 0 && (
            <div className={styles.statusMsg}>
              No history yet.<br />Generate a message to see it here.
            </div>
          )}

          {!loading &&
            history.map((item) => {
              return (
                <div key={item._id} className={styles.historyItem}>
                  <button
                    className={styles.itemContent}
                    onClick={() => handleSelect(item)}
                    title={`${item.input?.company || ""} — ${item.input?.role || ""}`}
                  >
                    <div className={styles.itemTop}>
                      <span className={styles.itemCompany}>
                        {item.input?.company || "Unknown"}
                      </span>
                      <span className={styles.itemDate}>
                        {formatDate(item.createdAt)}
                      </span>
                    </div>
                    <div className={styles.itemRole}>
                      {item.input?.role || "—"}
                    </div>
                    <div className={styles.itemPreview}>
                      {(item.output || "").slice(0, 80)}…
                    </div>
                  </button>
                  <div className={styles.statusRow}>
                    <select
                      className={`${styles.statusSelect} ${
                        item.applicationStatus
                          ? styles[`status_${item.applicationStatus}`]
                          : ""
                      }`}
                      value={item.applicationStatus || ""}
                      onChange={(e) => handleStatusChange(e, item._id)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label="Application status"
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.icon ? `${opt.icon} ${opt.label}` : opt.label}
                        </option>
                      ))}
                    </select>
                    {item.applicationStatus && (
                      <span className={`${styles.statusDot} ${styles[`dot_${item.applicationStatus}`]}`} />
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </>
  );
};

export default HistorySidebar;
